import {ensure, hasKey} from 'yougile-util'
import {findIdInWorkflow} from '../workflow-ids'
import {Condition} from './conditionals'
import {Context} from './context'
import {isUsersValue, usersValueToIds} from './users-value'
import {numberValueToNumber} from './number-value'

type Checker = (
  condition: Condition,
  ctx: Context
) => Promise<boolean | undefined>

export const checkEqCondition: Checker = async (condition, opts) => {
  if (!hasKey(condition, 'eq')) {
    return undefined
  }
  ensure(condition.eq instanceof Array, 'Incorrect eq condition')
  ensure(condition.eq?.length > 1, 'Incorrect eq condition', condition.eq)

  const first = await getValue(condition.eq[0], opts)
  const rest = condition.eq.slice(1)
  for (const v of rest) {
    if ((await getValue(v, opts)) !== first) {
      return false
    }
  }
  return true
}

const checkLessOrMoreCondition =
  (type: 'less' | 'more'): Checker =>
  async (condition, ctx) => {
    if (!hasKey(condition, type)) {
      return undefined
    }
    const arr = condition[type]
    ensure(arr instanceof Array, 'Incorrect less condition')
    if (arr.length === 0) {
      return false
    }
    let curr = numberValueToNumber(arr[0], ctx)
    ensure(typeof curr !== 'undefined', 'Incorrect number value', arr[0])
    const rest = arr.slice(1)
    for (const v of rest) {
      const next = numberValueToNumber(v, ctx)
      ensure(typeof next !== 'undefined', 'Incorrect number value', v)
      if (
        (type === 'less' && !(next > curr)) ||
        (type === 'more' && !(next < curr))
      ) {
        return false
      }
      curr = next
    }
    return true
  }
export const checkLessCondition = checkLessOrMoreCondition('less')
export const checkMoreCondition = checkLessOrMoreCondition('more')

export const checkContainsCondition: Checker = async (condition, opts) => {
  if (!hasKey(condition, 'contains')) {
    return undefined
  }
  ensure(condition.inside, 'Incorrect contains without inside property')
  const arr = await getArray(condition.inside, opts)
  const value = await getValue(condition.contains, opts)
  return arr?.includes(value)
}

const getArray = async (v: unknown, ctx: Context): Promise<unknown[]> => {
  const {workflow, task} = ctx
  if (v instanceof Array) {
    return Promise.all(v.map(item => getValue(item, ctx)))
  }
  if (hasKey(v, 'field')) {
    ensure(typeof v.field === 'string', 'Incorrect field value')
    const fieldId = findIdInWorkflow(workflow.ids, 'fields', v.field)
    ensure(fieldId, 'Field id not found', v.field)
    const value = task.data?.workflowState?.fields?.[fieldId]
    if (value instanceof Array) {
      return value
    } else {
      return []
    }
  }
  if (hasKey(v, 'values')) {
    ensure(v.values instanceof Array, 'Incorrect values list', v)
    return v.values
  }
  ensure(false, 'Unsupported list', v)
}

const getValue = async (v: unknown, ctx: Context): Promise<unknown> => {
  const {adapter} = ctx
  if (v instanceof Object) {
    if (hasKey(v, 'value')) {
      return v.value
    }
    if (hasKey(v, 'count')) {
      return (await getArray(v.count, ctx))?.length ?? null
    }
    if (isUsersValue(v)) {
      return (await usersValueToIds(v, adapter))?.[0]
    }
    if (hasKey(v, 'current')) {
      if (v.current === 'user') {
        return adapter.userId
      }
    }
  }
  if (
    typeof v === 'number' ||
    typeof v === 'string' ||
    typeof v === 'boolean'
  ) {
    return v
  }
  ensure(false, 'Unsupported value', v)
}
